import { resolveFollowUpThreadId } from '@littlebird/action-cards';
import { globalEventBus, EventNames } from '@littlebird/event-bus';
import {
  isVideoUpload,
  MAX_VIDEO_FILE_BYTES,
  attachmentTotalBytes,
  type AskUserAnswers,
  type ChatStatus,
  type Draft,
  type DraftFile,
  getDraftSendabilityError,
  isChatStatusBusy,
  isDraftEmpty,
  type JSONContent,
  RecordingStatus,
  useSDK,
  useStores,
} from '@littlebird/sdk';
import { useNavigate } from '@tanstack/react-router';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import {
  mentionedActionCardId,
  mentionedActionCardIds,
} from '@/features/action-cards/action-card-mention-source';
import { askUserStore } from '@/features/ask-user';
import { useChatContext } from '@/features/chat/chat-context';
import {
  ChatUnavailableError,
  performAttributedSend,
  registerComposerVoiceSend,
} from '@/features/chat/chat-send';
import {
  COMPOSER_FOCUS_REQUESTED,
  COMPOSER_START_NEW_THREAD,
  localChatEventsBus,
} from '@/features/chat/chat.events';
import { useCurrentChatMode } from '@/features/chat/chat.hooks';
import { chatStore, type QueuedChatMessage } from '@/features/chat/chat.store';
import { getDraftId } from '@/features/chat/composer-draft-id';
import { isNewChatDraftId } from '@/features/chat/draft-ids';
import { useQueueFeatures } from '@/features/chat/queued-messages';
import { useMessageQueue } from '@/features/chat/use-message-queue';
import { canUseSpeechFeatures } from '@/features/chat/voice/speech-features';
import { useVoiceModeTestDriver } from '@/features/chat/voice/voice-mode-dev-driver';
import { firstRunSummaryStore } from '@/features/context-collection';
import { mentionedGroupNames } from '@/features/hivemind/mentions';
import {
  FOLLOW_UP_SENT_TITLE,
  staysOnHomeAfterSend,
} from '@/features/home/follow-up-send-notice';
import {
  captureAppContexts,
  mentionedApps,
  type MentionItem,
} from '@/features/mention';
import { mentionedPersonIds } from '@/features/people-wiki/mentions';
import { useSpeechToText } from '@/features/speech-input/use-speech-to-text';
import { isOutOfCreditsBlocking } from '@/features/subscription';
import { useRegisterAttachFileHotkey } from '@/hooks/hotkeys/useRegisterAttachFileHotkey';
import { useRegisterBlurInputHotkey } from '@/hooks/hotkeys/useRegisterBlurInputHotkey';
import {
  useRegisterDashModeHotkey,
  useRegisterDefaultModeHotkey,
  useRegisterResearchModeHotkey,
  useRegisterUltraModeHotkey,
} from '@/hooks/hotkeys/useRegisterChatModesHotkeys';
import { useCallbackRef } from '@/hooks/useCallbackRef';
import { afterFrames } from '@/lib/animate';
import { logError } from '@/lib/errorLogging';
import { trackEvent } from '@/utils/analytics';
import { inBrowser, isMac } from '@/utils/capabilities';
import { shouldAttachClipboardFiles } from '@/utils/clipboard';
import {
  LONG_PASTE_THRESHOLD,
  MAX_ATTACHMENTS_PER_MESSAGE,
  MAX_TOTAL_ATTACHMENT_BYTES,
  NEW_CHAT_ID,
} from '@/utils/constants';
import { getFileFromPath } from '@/utils/file-io';
import { getImageDimensions, normalizeImageFormat } from '@/utils/images';

import {
  type ComposerEditorHandle,
  type ComposerEditorSelection,
  type ComposerPasteEvent,
} from './useComposerEditor';
import { useComposerProject } from './useComposerProject';
import { useRegisterSendChat } from './useRegisterSendChat';

/**
 * Capture on-screen context for the @-mentioned apps as `<context app="…">…</context>`
 * blocks. Never blocks the send: partial failures warn, a total failure sends without it.
 */
const captureMentionContext = async (
  mentions: MentionItem[]
): Promise<string> => {
  const apps = mentionedApps(mentions);
  if (apps.length === 0) return '';
  try {
    const { content, failedAppNames } = await captureAppContexts(apps);
    if (failedAppNames.length > 0) {
      toast.warning(
        `Couldn't read ${failedAppNames.join(', ')}. Continuing with the available app context.`
      );
    }
    return content;
  } catch (error) {
    logError(error, 'Failed to capture app-mention context', {
      context: 'Thread',
    });
    toast.warning("Couldn't read the mentioned apps. Sending without them.");
    return '';
  }
};

type DictationTarget = {
  draft: Draft;
  selection: ComposerEditorSelection | null;
  contentAtStart: string;
  documentAtStart: JSONContent | undefined;
  draftChangedBeforeTranscription: boolean;
};

function hasDraftChangedSinceDictationStarted(target: DictationTarget) {
  return (
    target.draft.content !== target.contentAtStart ||
    !isEqual(target.draft.draftDocument, target.documentAtStart)
  );
}

/** Send context that can change independently of the draft's typed content */
function getDraftSendContextKey(draft: Draft | undefined) {
  if (!draft) return null;
  return JSON.stringify({
    fileIds: draft.files.map((file) => file.id),
    selectedTexts: draft.selectedTexts,
    projectId: draft.projectId,
  });
}

export type ComposerCollapsed =
  | boolean
  | {
      collapsedHeight: number;
      collapsedVariant: 'card' | 'translucent';
    };

type UseComposerOptions = {
  threadId: string;
  setIsDraggingFile: (isDraggingFile: boolean) => void;
  isNewThread?: boolean;
  onThreadCreated?: (threadId: string) => void;
  onOpenThread?: (threadId: string) => void;
  projectId?: string | null;
  collapsed?: ComposerCollapsed;
  autoFocus?: boolean;
  /** A team chat's send: posts the message as the viewer, or answers 'ask' to send it to their own Littlebird */
  onSubmitOverride?: (
    text: string,
    mentions: MentionItem[],
    hasFiles: boolean
  ) => Promise<'posted' | 'failed' | 'ask'>;
};

type PerformSendOptions = {
  sourceDraftId?: string;
  mode?: string;
  mentions?: MentionItem[];
  askUserAnswers?: AskUserAnswers;
  onSendFailure?: () => void;
};

// Single-arg by design: a dependency on the animation tracker would both
// re-introduce the stranded-stop-icon bug and resubscribe the observer.
export const isComposerBusy = (threadStatus: ChatStatus | undefined): boolean =>
  isChatStatusBusy(threadStatus);

const COMPOSER_TOAST_OPTIONS = {
  position: 'top-right',
  closeButton: true,
} as const;

const addFileWithDimensions = async (
  draftsStore: ReturnType<typeof useStores>['drafts'],
  draftId: string,
  file: File
) => {
  const processedFile = await normalizeImageFormat(file);
  const dimensions = await getImageDimensions(processedFile);
  return draftsStore.addFileAndUpload(draftId, processedFile, dimensions);
};

const showFileUploadError = (error: unknown) => {
  toast.error('Error uploading file ', {
    description: error instanceof Error ? error.message : undefined,
  });
};

type UseFileUploadProps = {
  draftId: string;
  setIsDraggingFile: (isDraggingFile: boolean) => void;
};

/**
 * Open the browser file picker. Safari and Chromium report a dismissed sheet
 * through the `cancel` event (`abort` never fires for file inputs); without
 * listening for it the promise stays pending and the input leaks into the DOM.
 */
export function createBrowserFilePicker(): Promise<File[]> {
  return new Promise<File[]>((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.style.display = 'none';

    const cleanup = () => {
      if (document.body.contains(input)) {
        document.body.removeChild(input);
      }
    };

    input.onchange = () => {
      cleanup();
      resolve(Array.from(input.files ?? []));
    };

    input.oncancel = () => {
      cleanup();
      resolve([]);
    };

    input.onerror = () => {
      cleanup();
      resolve([]);
    };

    document.body.appendChild(input);
    input.click();
  });
}

const useFileUpload = ({ draftId, setIsDraggingFile }: UseFileUploadProps) => {
  const { drafts: draftsStore } = useStores();
  const draft = draftsStore.get(draftId);
  const attachmentsCount = draft?.files?.length ?? 0;
  const attachmentsSize = draft?.attachmentsSize ?? 0;
  const canAttachFile = draft?.canAttachFile ?? true;

  const showAttachmentLimitToast = useCallback(
    (
      type: 'count' | 'size' | 'video' = attachmentsCount <
      MAX_ATTACHMENTS_PER_MESSAGE
        ? 'count'
        : 'size',
      skipped?: number
    ) => {
      let description;

      switch (type) {
        case 'count':
          description =
            skipped && skipped > 0
              ? `${skipped === 1 ? 'A' : skipped} file${skipped > 1 ? 's' : ''} could not be attached. You can only attach up to ${MAX_ATTACHMENTS_PER_MESSAGE} files per message.`
              : `You can only attach up to ${MAX_ATTACHMENTS_PER_MESSAGE} files per message.`;
          break;

        case 'video':
          {
            const maxSizeMb = MAX_VIDEO_FILE_BYTES / 1024 / 1024;
            description =
              skipped && skipped > 0
                ? `${skipped} video${skipped > 1 ? 's' : ''} could not be attached. A video can be at most ${maxSizeMb} MB.`
                : `A video can be at most ${maxSizeMb} MB.`;
          }
          break;

        case 'size':
        default:
          {
            const maxSizeMb = MAX_TOTAL_ATTACHMENT_BYTES / 1024 / 1024;
            description =
              skipped && skipped > 0
                ? `${skipped === 1 ? 'A' : skipped} file${skipped > 1 ? 's' : ''} could not be attached because the total size exceeds the ${maxSizeMb} MB limit per message.`
                : `The total size of attachments cannot exceed ${maxSizeMb} MB per message.`;
          }
          break;
      }

      toast.error('Attachment limit reached', { description });
    },
    [attachmentsCount]
  );

  const showEmptyFileToast = useCallback((skipped?: number) => {
    toast.error('Attachment Error', {
      description:
        skipped && skipped > 0
          ? `${skipped === 1 ? 'An' : skipped} empty file${skipped > 1 ? 's' : ''} could not be attached.`
          : 'Empty files cannot be uploaded.',
    });
  }, []);

  const limitAttachmentsCount = useCallback(
    <T>(
      files: T[],
      onSkip: (skipped: number) => void = (skipped) =>
        showAttachmentLimitToast('count', skipped)
    ): T[] => {
      const allowed = Math.max(
        0,
        MAX_ATTACHMENTS_PER_MESSAGE - attachmentsCount
      );
      const result = files.slice(0, allowed);
      const skipped = files.length - result.length;

      if (skipped > 0) {
        onSkip(skipped);
      }

      return result;
    },
    [attachmentsCount, showAttachmentLimitToast]
  );

  const limitAttachmentsSize = useCallback(
    (
      files: File[],
      onSkip: (skipped: number) => void = (skipped) =>
        showAttachmentLimitToast('size', skipped)
    ) => {
      let bytesLeft = Math.max(0, MAX_TOTAL_ATTACHMENT_BYTES - attachmentsSize);

      const accepted: File[] = [];
      let skipped = 0;
      let skippedVideos = 0;

      for (const file of files) {
        if (isVideoUpload(file.name, file.type)) {
          if (file.size <= MAX_VIDEO_FILE_BYTES) {
            accepted.push(file);
          } else {
            skippedVideos++;
          }
          continue;
        }

        const size = attachmentTotalBytes(file);

        if (size <= bytesLeft) {
          accepted.push(file);
          bytesLeft -= size;
        } else {
          skipped++;
        }
      }

      if (skippedVideos > 0) showAttachmentLimitToast('video', skippedVideos);
      if (skipped > 0) onSkip(skipped);
      return accepted;
    },
    [attachmentsSize, showAttachmentLimitToast]
  );

  const handleNativeFileSelection = useCallback(async (): Promise<File[]> => {
    try {
      const result = await window.mainProcessApi.showOpenDialog({
        multiple: true,
        title: 'Select Files',
      });

      if (result.canceled || result.filePaths.length === 0) {
        return [];
      }

      const validFiles: File[] = [];
      let skippedCount = 0;
      let skippedSize = 0;
      let skippedVideos = 0;
      let skippedEmpty = 0;
      let bytesLeft = Math.max(0, MAX_TOTAL_ATTACHMENT_BYTES - attachmentsSize);

      for (const filePath of result.filePaths) {
        try {
          const file = await getFileFromPath(filePath);

          if (file.size === 0) {
            skippedEmpty++;
            continue;
          }

          // Check attachment count limit
          if (
            attachmentsCount + validFiles.length >=
            MAX_ATTACHMENTS_PER_MESSAGE
          ) {
            skippedCount++;
            continue;
          }

          if (isVideoUpload(file.name, file.type)) {
            if (file.size > MAX_VIDEO_FILE_BYTES) {
              skippedVideos++;
              continue;
            }
          } else {
            const size = attachmentTotalBytes(file);
            if (size > bytesLeft) {
              skippedSize++;
              continue;
            }
            bytesLeft -= size;
          }

          validFiles.push(file);
        } catch (error) {
          showFileUploadError(error);
          continue;
        }
      }

      if (skippedCount > 0) {
        showAttachmentLimitToast('count', skippedCount);
      }
      if (skippedSize > 0) {
        showAttachmentLimitToast('size', skippedSize);
      }
      if (skippedVideos > 0) {
        showAttachmentLimitToast('video', skippedVideos);
      }
      if (skippedEmpty > 0) {
        showEmptyFileToast(skippedEmpty);
      }

      return validFiles;
    } catch (error: any) {
      logError(error, 'Error showing Electron file dialog', {
        context: 'Thread',
      });
      toast.error('Error showing file selection dialog', {
        description: error.message ?? undefined,
      });
      return [];
    }
  }, [
    attachmentsCount,
    attachmentsSize,
    showAttachmentLimitToast,
    showEmptyFileToast,
  ]);

  const showFolderUploadToast = () =>
    toast.error('Attachment Error', {
      description:
        'Folder uploads are not supported. Please zip the folder or upload individual files.',
    });

  const handleClipboardBlobPaste = useCallback(
    async (clipboardData: DataTransfer) => {
      if (!canAttachFile) {
        showAttachmentLimitToast();
        return;
      }

      let files: Array<File> = [];
      let skippedEmpty = 0;

      for (const item of clipboardData.items) {
        try {
          const blob = item.getAsFile();
          if (!blob) continue;

          if (blob.size === 0) {
            skippedEmpty++;
            continue;
          }

          const file = new File([blob], blob.name, { type: blob.type });
          files.push(file);
        } catch (error) {
          logError(error, 'Error handling pasted image', {
            context: 'Thread',
          });
        }
      }

      if (skippedEmpty > 0) {
        showEmptyFileToast(skippedEmpty);
      }

      files = limitAttachmentsCount(limitAttachmentsSize(files));

      for (const file of files) {
        try {
          await addFileWithDimensions(draftsStore, draftId, file);
        } catch (error) {
          showFileUploadError(error);
        }
      }
    },
    [
      draftsStore,
      canAttachFile,
      draftId,
      showAttachmentLimitToast,
      limitAttachmentsCount,
      limitAttachmentsSize,
      showEmptyFileToast,
    ]
  );

  const isFilePickerOpenRef = useRef(false);

  const handleFileSelection = useCallback(async () => {
    if (isFilePickerOpenRef.current) {
      return;
    }
    if (!canAttachFile) {
      showAttachmentLimitToast();
      return;
    }

    isFilePickerOpenRef.current = true;
    try {
      if (inBrowser()) {
        const browserFiles = await createBrowserFilePicker();
        const files = browserFiles.filter((f) => f.size > 0);
        const skippedEmpty = browserFiles.length - files.length;

        if (skippedEmpty > 0) {
          showEmptyFileToast(skippedEmpty);
        }

        const processedFiles = limitAttachmentsCount(
          limitAttachmentsSize(files)
        );

        for (const file of processedFiles) {
          try {
            await addFileWithDimensions(draftsStore, draftId, file);
          } catch (error) {
            showFileUploadError(error);
          }
        }
      } else {
        let files = await handleNativeFileSelection();

        files = limitAttachmentsCount(limitAttachmentsSize(files));

        for (const file of files) {
          try {
            await addFileWithDimensions(draftsStore, draftId, file);
          } catch (error) {
            showFileUploadError(error);
          }
        }
      }
    } catch (error) {
      logError(error, 'Error selecting documents', { context: 'Thread' });
    } finally {
      isFilePickerOpenRef.current = false;
    }
  }, [
    canAttachFile,
    draftsStore,
    draftId,
    limitAttachmentsCount,
    limitAttachmentsSize,
    handleNativeFileSelection,
    showAttachmentLimitToast,
    showEmptyFileToast,
  ]);

  const handleDropBlob = useCallback(
    async (event: DragEvent) => {
      if (!canAttachFile) {
        showAttachmentLimitToast();
        return;
      }

      let files: Array<File> = [];
      let skippedEmpty = 0;

      if (event?.dataTransfer?.items) {
        for (const item of Array.from(event.dataTransfer.items)) {
          if (item.kind !== 'file') continue;

          const isDirectory = item?.webkitGetAsEntry?.()?.isDirectory ?? false;
          if (isDirectory) {
            showFolderUploadToast();
            continue;
          }

          const file = item.getAsFile();
          if (!file) continue;

          if (file.size === 0) {
            skippedEmpty++;
            continue;
          }

          files.push(file);
        }
      } else {
        for (const file of Array.from(event.dataTransfer?.files ?? [])) {
          if (file.size === 0) {
            skippedEmpty++;
            continue;
          }

          files.push(file);
        }
      }

      if (skippedEmpty > 0) {
        showEmptyFileToast(skippedEmpty);
      }

      files = limitAttachmentsCount(limitAttachmentsSize(files));

      for (const file of files) {
        try {
          await addFileWithDimensions(draftsStore, draftId, file);
        } catch (error) {
          showFileUploadError(error);
        }
      }
    },
    [
      canAttachFile,
      draftsStore,
      draftId,
      showAttachmentLimitToast,
      limitAttachmentsCount,
      limitAttachmentsSize,
      showEmptyFileToast,
    ]
  );

  const dragCounterRef = useRef(0);

  const checkFiles = (e: DragEvent) =>
    Array.from(e.dataTransfer?.types ?? []).includes('Files');

  const isProjectFileDrop = (e: DragEvent) =>
    e.target instanceof Element &&
    e.target.closest('[data-project-file-drop-zone]') !== null;

  useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      const hasFiles = checkFiles(e);

      if (!hasFiles) return;

      if (isProjectFileDrop(e)) {
        dragCounterRef.current = 0;
        setIsDraggingFile(false);
        return;
      }

      e.preventDefault();

      dragCounterRef.current += 1;

      setIsDraggingFile(true);
    };

    const handleDragLeave = (e: DragEvent) => {
      const hasFiles = checkFiles(e);

      if (!hasFiles || isProjectFileDrop(e)) return;

      e.preventDefault();

      dragCounterRef.current -= 1;

      if (dragCounterRef.current <= 0) {
        dragCounterRef.current = 0;
        setIsDraggingFile(false);
      }
    };

    const handleDragOver = (e: DragEvent) => {
      const hasFiles = checkFiles(e);

      if (!hasFiles) return;

      if (isProjectFileDrop(e)) {
        e.preventDefault();
        dragCounterRef.current = 0;
        setIsDraggingFile(false);
        return;
      }

      e.preventDefault();
    };

    const handleDrop = (e: DragEvent) => {
      const hasFiles = checkFiles(e);

      if (!hasFiles) return;

      e.preventDefault();

      dragCounterRef.current = 0;

      setIsDraggingFile(false);

      if (!isProjectFileDrop(e)) void handleDropBlob(e);
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', handleDrop);
    };
  }, [setIsDraggingFile, handleDropBlob]);

  return {
    handleClipboardBlobPaste,
    handleFileSelection,
  };
};

export const useComposer = ({
  isNewThread,
  threadId,
  setIsDraggingFile,
  onThreadCreated,
  onOpenThread,
  projectId,
  collapsed,
  autoFocus = true,
  onSubmitOverride,
}: UseComposerOptions) => {
  const { getSendContext, chatTitle } = useChatContext();
  const inputRef = useRef<HTMLElement | undefined>(undefined);
  const composerEditorHandleRef = useRef<ComposerEditorHandle | null>(null);
  const mentionsRef = useRef<MentionItem[]>([]);
  const isNewThreadRef = useRef(isNewThread);
  const sendStartInFlightRef = useRef(false);
  // Kill switch: with queueing off, a send on a busy thread is a no-op, nothing queued is
  // dispatched or shown, and the queued message's button is unavailable
  const { canQueueMessages, isSteeringOn } = useQueueFeatures();

  const {
    actionCards: actionCardsStore,
    drafts: draftsStore,
    threads: threadsStore,
    messages: messagesStore,
  } = useStores();
  const { voiceMode } = useSDK();
  const { currentChatMode: chatMode, setCurrentChatMode } =
    useCurrentChatMode();
  const navigate = useNavigate();
  const draftId = getDraftId(threadId, projectId);
  const draft = draftsStore.getDraft(draftId);
  const dictationTargetRef = useRef<DictationTarget | null>(null);
  const thread = threadsStore.getById(threadId);
  const {
    selectedProject,
    selectedProjectId,
    projectOptions,
    handleSelectProject,
    handleCreateProject,
    closeCreateProjectModal,
  } = useComposerProject({ draftId, threadId });
  const liveAskUserRequest = messagesStore.getLiveAskUserRequest(threadId);
  const askUserAnswersKey = liveAskUserRequest
    ? JSON.stringify(askUserStore.buildAnswers(liveAskUserRequest))
    : null;
  const composerSendOptionsRef = useRef({
    chatMode,
    selectedProjectId,
    askUserAnswersKey,
  });
  composerSendOptionsRef.current = {
    chatMode,
    selectedProjectId,
    askUserAnswersKey,
  };

  // No render-time read of animatingMessageIds: would resubscribe the
  // observer to claim/release. handleStopChat reads it lazily at click time.

  useEffect(() => {
    if (collapsed || !autoFocus) return;
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [collapsed, autoFocus]);

  const isBusy = isComposerBusy(thread?.status);
  const canAttachFile = draft?.canAttachFile ?? true;
  const attachmentsCount = draft?.files?.length ?? 0;

  const handleDictationTranscript = useCallback(
    (transcript: string) => {
      const target = dictationTargetRef.current;
      dictationTargetRef.current = null;
      // A removed New Chat draft can be recreated under the same ID before transcription returns
      if (
        !target ||
        draftsStore.getDraft(target.draft.draftId) !== target.draft
      ) {
        return;
      }
      const targetDraft = target.draft;
      const selection = target.selection;
      target.draftChangedBeforeTranscription =
        hasDraftChangedSinceDictationStarted(target);

      if (selection) {
        if (
          !target.draftChangedBeforeTranscription &&
          targetDraft.draftId === draftId &&
          composerEditorHandleRef.current?.insertTextAtSelection(
            transcript,
            selection
          )
        ) {
          afterFrames(5, () => inputRef.current?.focus());
          return;
        }

        const insertion = target.draftChangedBeforeTranscription
          ? selection.createTextInsertion(
              transcript,
              targetDraft.draftDocument,
              targetDraft.content
            )
          : selection.createTextInsertion(transcript);
        if (insertion) {
          draftsStore.updateDraft({
            draftId: targetDraft.draftId,
            content: insertion.content,
            draftDocument: insertion.document,
          });
          return;
        }
      }

      const currentContent = targetDraft.content;

      const newContent = currentContent
        ? `${currentContent} ${transcript}`
        : transcript;

      draftsStore.updateDraft({
        draftId: targetDraft.draftId,
        content: newContent,
      });

      if (targetDraft.draftId !== draftId) return;
      // Wait 5 frames to allow DOM updates and animations to settle
      afterFrames(5, () => {
        inputRef.current?.focus();
      });
    },
    [draftsStore, draftId]
  );

  // Dictation belongs to this composer; Voice Mode can continue after it unmounts
  const dictation = useSpeechToText(handleDictationTranscript);
  const startDictationRecording = dictation.startRecording;
  const startDictation = useCallback(() => {
    const targetDraft =
      draftsStore.getDraft(draftId) ?? draftsStore.createDraft({ draftId });
    dictationTargetRef.current = {
      draft: targetDraft,
      contentAtStart: targetDraft.content,
      documentAtStart: targetDraft.draftDocument,
      draftChangedBeforeTranscription: false,
      selection: composerEditorHandleRef.current?.getSelection() ?? null,
    };
    return startDictationRecording();
  }, [draftId, draftsStore, startDictationRecording]);
  const canUseVoiceMode = canUseSpeechFeatures();
  const isVoiceModeActive =
    canUseVoiceMode && voiceMode.isActiveForDraft(draftId);
  const recordingStatus = isVoiceModeActive
    ? voiceMode.recordingStatus
    : dictation.recordingStatus;

  const toggleVoiceMode = () => {
    if (!canUseVoiceMode) return;
    if (voiceMode.isActiveForDraft(draftId)) {
      voiceMode.stopSessionWithStopCue(draftId);
    } else {
      if (isNewChatDraftId(draftId) && !draftsStore.getDraft(draftId)) {
        // Keep the New Chat destination valid after this composer unmounts
        draftsStore.createDraft({ draftId });
      }
      voiceMode.startSession(draftId);
    }
  };

  const handleSavePastedText = useCallback(
    async (file: DraftFile, text: string) => {
      try {
        const updated = new File([text], file.fileName, {
          type: 'text/plain',
        });
        await draftsStore.replaceFileAndUpload(draftId, file, updated);
      } catch (error) {
        toast.error(
          'Could not save your edit. The pasted attachment is unchanged; try saving again.'
        );
        // rethrow so the dialog stays open with the edit intact
        throw error;
      }
    },
    [draftsStore, draftId]
  );

  const handleInsertPastedText = useCallback(
    async (file: DraftFile, text: string) => {
      try {
        const currentContent = draftsStore.getDraft(draftId)?.content ?? '';
        const content = `${currentContent}${text}`;

        if (draftsStore.getDraft(draftId)?.content !== content) {
          await draftsStore.updateDraft({ draftId, content });
        }
        await draftsStore.removeFile(draftId, file);

        afterFrames(5, () => inputRef.current?.focus());
      } catch (error) {
        toast.error(
          'Could not finish editing pasted text. The text may already be in the composer; remove the attachment manually if needed.'
        );
        throw error;
      }
    },
    [draftsStore, draftId]
  );

  /** Answers picked but not yet sent make an empty note sendable on their own */
  const hasAnswersForThread = useCallback(() => {
    const request = messagesStore.getLiveAskUserRequest(threadId);
    return Boolean(request && askUserStore.hasAnyAnswer(request.request_id));
  }, [messagesStore, threadId]);
  // Read during render so the Send button re-enables the moment an option is picked
  const hasAskUserAnswers = hasAnswersForThread();

  // Keep Send enabled during Voice Mode so typed text can cancel its current capture
  const isBlockedByCredits = isOutOfCreditsBlocking();
  const hasSendableInput = draft
    ? getDraftSendabilityError(draft, { hasAskUserAnswers }) === null
    : hasAskUserAnswers;
  const hasActiveDictation =
    !isVoiceModeActive && recordingStatus !== RecordingStatus.None;
  const isSendDisabled =
    (!hasSendableInput && !hasActiveDictation) || isBlockedByCredits;
  const isComposerEmpty =
    (!draft || isDraftEmpty(draft)) &&
    !hasAskUserAnswers &&
    !hasActiveDictation;

  /**
   * Recover from a send into a chat that no longer accepts messages - deleted
   * or archived while its composer was still open here (for example, from
   * another device). The unsent words are not lost: the chat's draft, plus any
   * spoken transcript that failed to send, moves to the global New Chat draft
   * for review. If a New Chat draft already exists it is kept and this chat's
   * words are dropped instead, since unrelated drafts cannot be merged. Either
   * way an explanatory toast shows and the app navigates to New Chat.
   */
  const recoverUnavailableChat = useCallback(
    async (voiceTranscript?: string) => {
      closeCreateProjectModal();
      voiceMode.stopSession(draftId);
      const recoveredDraftId = getDraftId(NEW_CHAT_ID, null);
      const unavailableDraft = draftsStore.getDraft(draftId);
      const existingNewChatDraft = draftsStore.getDraft(recoveredDraftId);
      if (existingNewChatDraft) {
        // Keep the existing New Chat draft because unrelated drafts cannot be merged safely
        if (unavailableDraft) draftsStore.removeDraft(unavailableDraft);
      } else {
        await draftsStore.moveDraft(draftId, recoveredDraftId);
        if (voiceTranscript !== undefined) {
          await draftsStore.updateDraft({
            draftId: recoveredDraftId,
            content: unavailableDraft?.content.trim()
              ? `${unavailableDraft.content}\n\n${voiceTranscript}`
              : voiceTranscript,
          });
        }
      }
      toast.error('Chat unavailable', {
        position: 'top-right',
        closeButton: true,
        description:
          existingNewChatDraft === undefined
            ? 'Your message was not sent because this chat is no longer available. Review it in New Chat before sending.'
            : 'Your message was not sent because this chat is no longer available. Your existing New Chat draft was kept instead.',
      });
      navigate({ to: '/chats', search: { new: true } });
    },
    [closeCreateProjectModal, draftId, draftsStore, navigate, voiceMode]
  );

  /**
   * Shared core of the typed and spoken sends. `performAttributedSend`
   * prepares meeting chats, emits send events, and starts the SDK send.
   */
  const performSend = useCallback(
    async (
      voiceTranscript?: string,
      sendOptions: PerformSendOptions = {}
    ): Promise<boolean> => {
      const isVoiceSend = voiceTranscript !== undefined;
      const voiceModeActiveNow = voiceMode.isActiveForDraft(draftId);
      const sendDraftId = sendOptions.sourceDraftId ?? draftId;
      const sendMode = sendOptions.mode ?? chatMode;
      const sendMentions = sendOptions.mentions ?? mentionsRef.current;
      const followUpActionCardIds = isVoiceSend
        ? []
        : mentionedActionCardIds(sendMentions);
      const staysOnHome = staysOnHomeAfterSend(
        threadId,
        followUpActionCardIds.length
      );
      const cardAnalyticsContext = {
        surface: staysOnHome ? ('home' as const) : ('chat' as const),
      };

      if (isNewThreadRef.current) {
        isNewThreadRef.current = false;
        firstRunSummaryStore.settleEmptyOnSend();
      }

      // Attach on-screen context for any @-mentioned apps (macOS-only editor
      // feature; typed sends only — a spoken transcript isn't the typed draft).
      const appContext =
        isMac() && !isVoiceSend
          ? await captureMentionContext(sendMentions)
          : '';

      // Like app context, a spoken transcript isn't the typed draft the mentions sit in
      const personIds = isVoiceSend ? [] : mentionedPersonIds(sendMentions);
      const groupNames = isVoiceSend ? [] : mentionedGroupNames(sendMentions);
      // A queued dispatch stays on the thread whose queue it is serialized against
      const isQueuedDispatch = sendOptions.sourceDraftId !== undefined;
      const continuedThreadId = isQueuedDispatch
        ? null
        : resolveFollowUpThreadId(
            followUpActionCardIds.flatMap(
              (cardId) => actionCardsStore.get(cardId) ?? []
            ),
            (candidateId) => {
              const candidate = threadsStore.getById(candidateId);
              return !!candidate && !candidate.deletedAt;
            }
          );
      const sendTargetThreadId =
        threadId === NEW_CHAT_ID && continuedThreadId
          ? continuedThreadId
          : threadId;
      // The shared send path updates Voice Mode; this callback shows the error
      const handleSendFailure = (error: unknown) => {
        toast.error('Error', {
          position: 'top-right',
          closeButton: true,
          description:
            error instanceof Error ? error.message : 'Error sending message',
        });
        logError(error, 'Error sending message', {
          context: 'Thread',
          metadata: {
            threadId,
            threadExists: Boolean(thread),
            threadSynced: thread?.synced ?? null,
          },
        });
      };

      // Captured before dispatch: once the outgoing message lands in the store,
      // `getLiveAskUserRequest` no longer finds the request that was answered
      let answeredAskUserRequestId: string | undefined;

      try {
        const dispatch = await performAttributedSend({
          threadId: sendTargetThreadId,
          mode: sendMode,
          // Request speech for typed messages that continue an active Voice Mode conversation
          requestResponseAudio: isVoiceSend || voiceModeActiveNow,
          content: voiceTranscript,
          cardAnalyticsContext,
          prepareExtras: (sendThreadId) => {
            // If meeting preparation retargeted this send (`sendThreadId` !==
            // `sendTargetThreadId`), it also moved the draft to the retargeted thread
            const preparedDraftId =
              sendThreadId === sendTargetThreadId ? sendDraftId : sendThreadId;
            const askUserRequest =
              messagesStore.getLiveAskUserRequest(sendThreadId);
            answeredAskUserRequestId = askUserRequest?.request_id ?? undefined;
            const preparedAskUserAnswers =
              sendOptions.askUserAnswers ??
              (askUserRequest
                ? (askUserStore.buildAnswers(askUserRequest) ?? undefined)
                : undefined);
            // If the typed draft is unsendable, throw before analytics count the send
            if (!isVoiceSend) {
              draftsStore.validateDraft(preparedDraftId, {
                hasAskUserAnswers: Boolean(preparedAskUserAnswers),
              });
            }
            const context =
              [getSendContext?.(sendThreadId), appContext]
                .filter(Boolean)
                .join('\n') || undefined;
            return {
              draftId: preparedDraftId,
              title: chatTitle,
              context,
              projectId: selectedProjectId,
              askUserAnswers: preparedAskUserAnswers,
              sourceActionCardId: mentionedActionCardId(sendMentions),
              followUpActionCardIds,
              personIds,
              groupNames,
            };
          },
          onMeetingThreadPrepared: onThreadCreated,
        });

        if (answeredAskUserRequestId) {
          askUserStore.clear(answeredAskUserRequestId);
        }

        if (dispatch.createdThreadId !== null) {
          closeCreateProjectModal();
          if (isVoiceSend && !dispatch.wasPreCreatedThread) {
            // The untouched typed draft follows the conversation to the new thread
            await draftsStore.moveDraft(draftId, dispatch.createdThreadId);
          }
          onThreadCreated?.(dispatch.createdThreadId);
          if (onOpenThread) onOpenThread(dispatch.createdThreadId);
          else if (!dispatch.wasPreCreatedThread && !staysOnHome)
            navigate({ to: `/chats/${dispatch.createdThreadId}` });
        } else {
          // A cancelled stream is a Stop or Interrupt, not a failure: holding the
          // queue for it would strand the messages a successful Interrupt just released
          void dispatch.sendPromise
            .then((outcome) => {
              if (outcome === 'failed') sendOptions.onSendFailure?.();
            })
            .catch((error) => {
              handleSendFailure(error);
              sendOptions.onSendFailure?.();
            });
          if (sendTargetThreadId !== threadId) {
            onThreadCreated?.(sendTargetThreadId);
            if (onOpenThread) onOpenThread(sendTargetThreadId);
            else if (!staysOnHome)
              navigate({ to: `/chats/${sendTargetThreadId}` });
          }
        }

        if (staysOnHome) {
          const sentThreadId = dispatch.createdThreadId ?? sendTargetThreadId;
          toast.success(FOLLOW_UP_SENT_TITLE, {
            position: 'top-right',
            action: {
              label: 'Open chat',
              onClick: () => navigate({ to: `/chats/${sentThreadId}` }),
            },
          });
        }
      } catch (error) {
        if (error instanceof ChatUnavailableError) {
          await recoverUnavailableChat(voiceTranscript);
          return false;
        }
        handleSendFailure(error);
        return false;
      }
      return true;
    },
    [
      actionCardsStore,
      draftId,
      draftsStore,
      messagesStore,
      thread,
      threadId,
      threadsStore,
      chatMode,
      navigate,
      selectedProjectId,
      getSendContext,
      chatTitle,
      closeCreateProjectModal,
      onThreadCreated,
      onOpenThread,
      recoverUnavailableChat,
      voiceMode,
    ]
  );

  const performSendWithLock = useCallback(
    async (
      voiceTranscript?: string,
      sendOptions: PerformSendOptions = {}
    ): Promise<boolean> => {
      if (sendStartInFlightRef.current) return false;
      sendStartInFlightRef.current = true;
      try {
        return await performSend(voiceTranscript, sendOptions);
      } finally {
        sendStartInFlightRef.current = false;
      }
    },
    [performSend]
  );

  /** Send the saved draft after any dictation in this composer finishes */
  const cancelDictation = dictation.cancelRecording;
  const stopDictation = dictation.stopRecording;
  const isDictationCapturing = dictation.isCapturing;
  const hasPendingDictationTranscription = dictation.hasPendingTranscription;
  const sendMessage = useCallback(async () => {
    const overrideDraft = onSubmitOverride && draftsStore.getDraft(draftId);
    const overrideText = overrideDraft?.content.trim() ?? '';
    if (onSubmitOverride && overrideText !== '') {
      const outcome = await onSubmitOverride(
        overrideText,
        [...mentionsRef.current],
        (overrideDraft?.files.length ?? 0) > 0
      );
      if (outcome === 'failed') return;
      if (outcome === 'posted') {
        const posted = draftsStore.getDraft(draftId);
        if (posted) draftsStore.removeDraft(posted);
        mentionsRef.current = [];
        return;
      }
    }
    if (
      sendStartInFlightRef.current ||
      isComposerBusy(threadsStore.getById(threadId)?.status)
    ) {
      return;
    }

    sendStartInFlightRef.current = true;
    try {
      const voiceModeActive = voiceMode.isActiveForDraft(draftId);
      if (
        !voiceModeActive &&
        (isDictationCapturing() || hasPendingDictationTranscription())
      ) {
        const dictationTarget = dictationTargetRef.current;
        const dictationDraft =
          dictationTarget?.draft ?? draftsStore.getDraft(draftId);
        const sendContextAtClick = getDraftSendContextKey(dictationDraft);
        const sendOptionsAtClick = composerSendOptionsRef.current;
        if (recordingStatus === RecordingStatus.Recording) {
          trackEvent('MESSAGE_USER_VOICE');
        }
        const dictationOutcome = await stopDictation();
        if (dictationTarget && dictationTargetRef.current === dictationTarget) {
          dictationTargetRef.current = null;
          if (hasDraftChangedSinceDictationStarted(dictationTarget)) return;
        }
        if (dictationOutcome !== 'completed') return;
        // Other composer controls remain interactive while transcription is
        // pending. If their send context changed after the click, preserve it
        // for review instead of dispatching something the user did not submit.
        if (
          getDraftSendContextKey(dictationDraft) !== sendContextAtClick ||
          composerSendOptionsRef.current.chatMode !==
            sendOptionsAtClick.chatMode ||
          composerSendOptionsRef.current.selectedProjectId !==
            sendOptionsAtClick.selectedProjectId ||
          composerSendOptionsRef.current.askUserAnswersKey !==
            sendOptionsAtClick.askUserAnswersKey
        ) {
          return;
        }
        // The transcript callback rejects a draft that was replaced while it
        // was pending. Do not then send that unrelated replacement draft.
        if (draftsStore.getDraft(draftId) !== dictationDraft) return;
        // If the same draft changed elsewhere while transcription was pending,
        // keep the transcript but let the user review the merged draft.
        if (dictationTarget?.draftChangedBeforeTranscription) return;
      } else {
        cancelDictation();
      }

      // Re-check the live draft rather than this render's snapshot
      const answersReady = hasAnswersForThread();
      // Answering without typing leaves no draft behind, and the rest of the send
      // path is built around one existing, so materialize an empty draft here
      if (!draftsStore.getDraft(draftId) && answersReady) {
        await draftsStore.updateDraft({ draftId, content: '' });
      }
      const currentDraft = draftsStore.getDraft(draftId);
      if (
        isOutOfCreditsBlocking() ||
        !currentDraft ||
        getDraftSendabilityError(currentDraft, {
          hasAskUserAnswers: answersReady,
        }) !== null
      ) {
        return;
      }
      voiceMode.discardActiveCaptureForTypedSend(draftId);
      await performSend();
    } finally {
      sendStartInFlightRef.current = false;
    }
  }, [
    cancelDictation,
    draftId,
    draftsStore,
    hasAnswersForThread,
    hasPendingDictationTranscription,
    isDictationCapturing,
    onSubmitOverride,
    performSend,
    recordingStatus,
    stopDictation,
    threadId,
    threadsStore,
    voiceMode,
  ]);

  /** A send has to wait while the turn runs or while older messages are queued, held, or dispatching */
  const mustQueue = useCallback(
    () =>
      isComposerBusy(threadsStore.getById(threadId)?.status) ||
      chatStore.hasQueueState(threadId),
    [threadId, threadsStore]
  );

  const queueMessage = useCallback(async (): Promise<
    QueuedChatMessage | undefined
  > => {
    if (sendStartInFlightRef.current || !mustQueue()) return undefined;

    sendStartInFlightRef.current = true;
    try {
      const askUserRequest = messagesStore.getLiveAskUserRequest(threadId);
      const askUserAnswers = askUserRequest
        ? (askUserStore.buildAnswers(askUserRequest) ?? undefined)
        : undefined;
      if (!draftsStore.getDraft(draftId) && askUserAnswers) {
        await draftsStore.updateDraft({ draftId, content: '' });
      }
      const currentDraft = draftsStore.getDraft(draftId);
      if (
        isOutOfCreditsBlocking() ||
        !currentDraft ||
        getDraftSendabilityError(currentDraft, {
          hasAskUserAnswers: Boolean(askUserAnswers),
        }) !== null
      ) {
        return undefined;
      }

      const id = uuidv4();
      const queuedMessage: QueuedChatMessage = {
        id,
        draftId: `queued:${threadId}:${id}`,
        content: currentDraft.content,
        attachmentCount: currentDraft.files.length,
        selectedTextCount: currentDraft.selectedTexts.length,
        mode: chatMode,
        mentions: [...mentionsRef.current],
        askUserAnswers,
      };
      await draftsStore.moveDraft(draftId, queuedMessage.draftId);
      chatStore.enqueueMessage(threadId, queuedMessage);
      mentionsRef.current = [];
      if (askUserRequest) askUserStore.clear(askUserRequest.request_id);
      return queuedMessage;
    } finally {
      sendStartInFlightRef.current = false;
    }
  }, [chatMode, draftId, draftsStore, messagesStore, mustQueue, threadId]);

  const submitMessage = useCallback(async () => {
    if (canQueueMessages && mustQueue()) {
      await queueMessage();
      return;
    }
    await sendMessage();
  }, [canQueueMessages, mustQueue, queueMessage, sendMessage]);

  const stopActiveTurn = useCallback(async (): Promise<boolean> => {
    voiceMode.stopSession(draftId);
    const animatingMessageId = chatStore.getAnimatingMessageId(threadId);
    chatStore.clearAnimatingSlot(threadId);
    const stopPromise = threadsStore.stopStreamingForThread(threadId);
    const messageStreamsStopped =
      messagesStore.abortActiveStreamsForThread(threadId);
    threadsStore.abortActiveStream(threadId);
    await globalEventBus.emit(EventNames.CHAT_STREAMING_MESSAGE_WILL_STOP, {
      data: { threadId, messageId: animatingMessageId },
    });
    const [stopped] = await Promise.all([stopPromise, messageStreamsStopped]);
    return stopped;
  }, [draftId, messagesStore, threadId, threadsStore, voiceMode]);

  const startQueuedSend = useCallback(
    (sendThreadId: string, message: QueuedChatMessage) =>
      performSendWithLock(undefined, {
        sourceDraftId: message.draftId,
        mode: message.mode,
        mentions: message.mentions,
        askUserAnswers: message.askUserAnswers,
        onSendFailure: () => chatStore.holdQueue(sendThreadId, 'send_failed'),
      }),
    [performSendWithLock]
  );
  const {
    sendNowAction,
    canSendNow,
    hasQueuedMessage,
    isSendingNow: isSendingQueuedMessageNow,
    sendNow: handleSendNow,
    removeQueuedMessage: handleRemoveQueuedMessage,
  } = useMessageQueue({
    threadId,
    isBusy,
    canQueueMessages,
    isSteeringOn,
    isAutoSendPaused: isBlockedByCredits,
    startSend: startQueuedSend,
    stopResponse: stopActiveTurn,
    queueDraft: queueMessage,
    getSteerContext: getSendContext,
    toastOptions: COMPOSER_TOAST_OPTIONS,
  });

  /** Send one spoken transcript without using the typed draft as message content */
  const sendVoiceTranscript = useCallbackRef(async (transcript: string) => {
    if (!transcript.trim()) return;
    await performSend(transcript);
  });
  useEffect(() => {
    // Reuse this input's project, context, and draft setup for spoken messages
    return registerComposerVoiceSend(draftId, sendVoiceTranscript);
  }, [draftId, sendVoiceTranscript]);

  useEffect(() => {
    const subscription = localChatEventsBus.once(
      COMPOSER_START_NEW_THREAD,
      async ({ data: { message } }) => {
        if (!isNewThreadRef.current) return;

        draftsStore.createDraft({ draftId, content: message });
        await sendMessage();
      }
    );

    return () => subscription.unsubscribe();
  }, [draftId, draftsStore, sendMessage]);

  useEffect(() => {
    const subscription = localChatEventsBus.on(
      COMPOSER_FOCUS_REQUESTED,
      ({ data }) => {
        if (data.draftId !== draftId) return;
        const focusAtEnd = () => composerEditorHandleRef.current?.focusAtEnd();
        focusAtEnd();
        afterFrames(2, focusAtEnd);
      }
    );
    return () => subscription.unsubscribe();
  }, [draftId]);

  const blurInputHotkeyRef = useRegisterBlurInputHotkey(inputRef);

  const sendStopChatHotkeyRef = useRegisterSendChat(inputRef, {
    isDisabled: isSendDisabled,
    canSendNow,
    hasQueuedMessage,
    onSendChat: submitMessage,
    onSendNow: handleSendNow,
  });

  useRegisterDefaultModeHotkey({
    mode: chatMode,
    setMode: setCurrentChatMode,
  });
  useRegisterResearchModeHotkey({
    mode: chatMode,
    setMode: setCurrentChatMode,
  });
  useRegisterUltraModeHotkey({
    mode: chatMode,
    setMode: setCurrentChatMode,
  });
  useRegisterDashModeHotkey({
    mode: chatMode,
    setMode: setCurrentChatMode,
  });

  const { handleClipboardBlobPaste, handleFileSelection } = useFileUpload({
    draftId,
    setIsDraggingFile,
  });

  useRegisterAttachFileHotkey(inputRef, {
    onAttachFile: handleFileSelection,
    enabled: canAttachFile,
  });

  const setDraftContent = useCallback(
    (content: string, document?: JSONContent) => {
      draftsStore.updateDraft(
        document
          ? { draftId, content, draftDocument: document }
          : { draftId, content }
      );
    },
    [draftsStore, draftId]
  );

  /** Returns true when the paste was consumed and the input must not insert it */
  const handlePaste = useCallback(
    (e: ComposerPasteEvent) => {
      const clipboardData = e.clipboardData;
      if (!clipboardData) return false;

      if (shouldAttachClipboardFiles(clipboardData)) {
        e.preventDefault();
        handleClipboardBlobPaste(clipboardData);
        return true;
      }

      const text = clipboardData.getData('text/plain');

      if (text.length > LONG_PASTE_THRESHOLD) {
        e.preventDefault();
        const timestamp = new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/[:-]/g, '');
        const fileName = `pasted-text-${timestamp}.txt`;
        const file = new File([text], fileName, { type: 'text/plain' });
        draftsStore.addFileAndUpload(draftId, file);
        return true;
      }

      // short text pastes natively so cmd+z can undo them; onChange
      // syncs the draft
      return false;
    },
    [handleClipboardBlobPaste, draftsStore, draftId]
  );

  const resendQuery = useCallback(async () => {
    try {
      if (!thread) {
        throw new Error('Thread does not exist');
      }
      if (!thread.synced) {
        await threadsStore.retryThreadInitialization({
          thread,
          mode: chatMode,
          useTodos: false,
        });
        return;
      }
      const lastMessageId = thread.lastMessageId;
      if (!lastMessageId) {
        throw new Error(
          'No message in thread to retry. Please send a new message'
        );
      }
      await messagesStore.retryMessage({
        threadId,
        messageId: lastMessageId,
        mode: chatMode,
        useTodos: false,
      });
    } catch (error) {
      toast.error('Error', {
        position: 'top-right',
        closeButton: true,
        description:
          error instanceof Error ? error.message : 'Unable to retry message',
      });
      logError(error, 'Error resending message', { context: 'Thread' });
    }
  }, [thread, messagesStore, threadId, chatMode, threadsStore]);

  const handleStopChat = useCallback(async () => {
    voiceMode.stopSession(draftId);
    const animatingMessageId = chatStore.getAnimatingMessageId(threadId);
    chatStore.clearAnimatingSlot(threadId);

    // Stop the LLM stream and abort a send the server has not accepted yet
    threadsStore.stopStreamingForThread(threadId);
    const messageStreamsStopped =
      messagesStore.abortActiveStreamsForThread(threadId);
    threadsStore.abortActiveStream(threadId);
    await messageStreamsStopped;

    // Stop the current message's typewriter after cancelling its response stream
    await globalEventBus.emit(EventNames.CHAT_STREAMING_MESSAGE_WILL_STOP, {
      data: {
        threadId,
        messageId: animatingMessageId,
      },
    });

    trackEvent('CHAT_STOP_GENERATION');
  }, [draftId, messagesStore, threadId, threadsStore, voiceMode]);

  useVoiceModeTestDriver({
    threadId,
    draftId,
    draftsStore,
    sendMessage,
  });

  return {
    draft,
    draftId,
    thread,
    isBusy,
    isBlockedByCredits,
    isComposerEmpty,
    isSendDisabled,
    sendMessage,
    submitMessage,
    canQueueMessages,
    sendNowAction,
    handleSendNow,
    handleRemoveQueuedMessage,
    isSendingQueuedMessageNow,
    setDraftContent,
    handlePaste,
    resendQuery,
    recordingStatus,
    startRecording: startDictation,
    stopRecording: dictation.stopRecording,
    cancelRecording: dictation.cancelRecording,
    canUseVoiceMode,
    isVoiceModeActive,
    toggleVoiceMode,
    dictationMediaStreamRef: dictation.mediaStreamRef,
    handleFileSelection,
    handleSavePastedText,
    handleInsertPastedText,
    blurInputHotkeyRef,
    sendStopChatHotkeyRef,
    inputRef,
    composerEditorHandleRef,
    mentionsRef,
    threadId,
    handleStopChat,
    canAttachFile,
    attachmentsCount,
    selectedProject,
    projectOptions,
    handleSelectProject,
    handleCreateProject,
  };
};
